import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Snackbar, Alert } from "@mui/material";
import {
  fetchCustomers,
  createCustomer,
  updateCustomer,
  deleteCustomer,
} from "../redux/customerSlice";
import { fetchChildrenByCustomer, createChild } from "../redux/childSlice";
import "./css/AdminCustomersPage.css";

const Modal = ({ children, isOpen, onClose }) => {
  if (!isOpen) return null;

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      {children}
    </div>,
    document.body
  );
};

const AdminCustomersPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { items: customers, loading } = useSelector((state) => state.customers);
  const { items: children } = useSelector((state) => state.children);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [openDialog, setOpenDialog] = useState(false);
  const [openChildDialog, setOpenChildDialog] = useState(false);
  const [openBulkSmsDialog, setOpenBulkSmsDialog] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [bulkSmsMessage, setBulkSmsMessage] = useState("");

  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
    address: "",
    notes: "",
    status: "active",
  });

  const [childFormData, setChildFormData] = useState({
    name: "",
    birthDate: "",
    gender: "",
    diagnosis: "",
    medicalNotes: "",
    specialNeeds: "",
    notes: "",
  });

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);

  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });

  useEffect(() => {
    dispatch(fetchCustomers({}));
  }, [dispatch]);

  // Modal açıkken body scroll'unu engelle
  useEffect(() => {
    const isModalOpen =
      openDialog || openChildDialog || openBulkSmsDialog || confirmDelete;

    if (isModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [openDialog, openChildDialog, openBulkSmsDialog, confirmDelete]);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
  };

  const handleOpenDialog = (customer = null) => {
    if (customer) {
      setEditingCustomer(customer);
      setFormData({
        fullName: customer.fullName,
        phone: customer.phone,
        email: customer.email || "",
        address: customer.address || "",
        notes: customer.notes || "",
        status: customer.status,
      });
    } else {
      setEditingCustomer(null);
      setFormData({
        fullName: "",
        phone: "",
        email: "",
        address: "",
        notes: "",
        status: "active",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCustomer(null);
  };

  const handleSubmit = async () => {
    try {
      if (editingCustomer) {
        await dispatch(
          updateCustomer({ id: editingCustomer._id, data: formData })
        ).unwrap();
        showToast("Müşteri güncellendi");
      } else {
        await dispatch(createCustomer(formData)).unwrap();
        showToast("Müşteri oluşturuldu");
      }
      handleCloseDialog();
      dispatch(fetchCustomers({}));
    } catch (error) {
      showToast(error || "İşlem başarısız", "error");
    }
  };

  const handleOpenChildDialog = (customerId) => {
    setSelectedCustomerId(customerId);
    dispatch(fetchChildrenByCustomer(customerId));
    setChildFormData({
      name: "",
      birthDate: "",
      gender: "",
      diagnosis: "",
      medicalNotes: "",
      specialNeeds: "",
      notes: "",
    });
    setOpenChildDialog(true);
  };

  const handleChildSubmit = async () => {
    try {
      await dispatch(
        createChild({ ...childFormData, customerId: selectedCustomerId })
      ).unwrap();
      showToast("Çocuk eklendi");
      setOpenChildDialog(false);
    } catch (error) {
      showToast(error || "Çocuk eklenemedi", "error");
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await dispatch(deleteCustomer(customerToDelete)).unwrap();
      showToast("Müşteri silindi", "info");
      dispatch(fetchCustomers({}));
    } catch {
      showToast("Silme başarısız", "error");
    } finally {
      setConfirmDelete(false);
      setCustomerToDelete(null);
    }
  };

  const handleSelectCustomer = (customerId) => {
    setSelectedCustomers((prev) =>
      prev.includes(customerId)
        ? prev.filter((id) => id !== customerId)
        : [...prev, customerId]
    );
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedCustomers(filteredCustomers.map((c) => c._id));
    } else {
      setSelectedCustomers([]);
    }
  };

  const handleBulkSms = async () => {
    try {
      if (!bulkSmsMessage.trim()) {
        showToast("Lütfen bir mesaj yazın", "error");
        return;
      }

      // API endpoint'ine istek gönder
      const response = await fetch(
        "https://api.dilanaslan.com/api/messages/bulk",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            customerIds: selectedCustomers,
            message: bulkSmsMessage,
          }),
        }
      );

      const result = await response.json();

      if (result.success) {
        showToast(result.message, "success");
        setBulkSmsMessage("");
        setSelectedCustomers([]);
        setOpenBulkSmsDialog(false);
      } else {
        showToast(result.message || "SMS gönderilemedi", "error");
      }
    } catch (error) {
      console.error("Toplu SMS gönderme hatası:", error);
      showToast("SMS gönderilirken bir hata oluştu", "error");
    }
  };

  const filteredCustomers = customers.filter((customer) => {
    const matchesSearch =
      customer.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.phone.includes(searchTerm) ||
      (customer.email &&
        customer.email.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus =
      statusFilter === "all" || customer.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  return (
    <div className="admin-customers-page">
      <div className="page-container">
        <div className="page-header">
          <h1 className="page-title">Müşteri Yönetimi</h1>
          <div className="header-actions">
            {selectedCustomers.length > 0 && (
              <button
                className="btn btn-outlined"
                onClick={() => setOpenBulkSmsDialog(true)}
              >
                <span className="btn-icon">📨</span>
                Toplu SMS ({selectedCustomers.length})
              </button>
            )}
            <button
              className="btn btn-primary"
              onClick={() => handleOpenDialog()}
            >
              <span className="btn-icon">➕</span>
              Yeni Müşteri
            </button>
          </div>
        </div>

        <div className="filters-section">
          <div className="search-box">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Müşteri ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <div className="filter-group">
            <label htmlFor="status-filter">Durum</label>
            <select
              id="status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">Tümü</option>
              <option value="active">Aktif</option>
              <option value="inactive">Pasif</option>
            </select>
          </div>
        </div>

        <div className="table-container">
          <table className="customers-table">
            <thead>
              <tr>
                <th className="checkbox-cell">
                  <input
                    type="checkbox"
                    checked={
                      filteredCustomers.length > 0 &&
                      selectedCustomers.length === filteredCustomers.length
                    }
                    onChange={handleSelectAll}
                  />
                </th>
                <th>Ad Soyad</th>
                <th>Telefon</th>
                <th>E-posta</th>
                <th>Durum</th>
                <th>Kayıt Tarihi</th>
                <th className="actions-cell">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.map((customer) => (
                <tr key={customer._id}>
                  <td className="checkbox-cell">
                    <input
                      type="checkbox"
                      checked={selectedCustomers.includes(customer._id)}
                      onChange={() => handleSelectCustomer(customer._id)}
                    />
                  </td>
                  <td>
                    <div className="customer-name-cell">
                      <span className="icon">👤</span>
                      <button
                        className="customer-name-link"
                        onClick={() =>
                          navigate(`/admin/customers/${customer._id}/sessions`)
                        }
                      >
                        {customer.fullName}
                      </button>
                    </div>
                  </td>
                  <td>
                    <div className="cell-with-icon">
                      <span className="icon">📞</span>
                      {customer.phone}
                    </div>
                  </td>
                  <td>
                    {customer.email && (
                      <div className="cell-with-icon">
                        <span className="icon">✉️</span>
                        {customer.email}
                      </div>
                    )}
                  </td>
                  <td>
                    <span
                      className={`status-badge ${
                        customer.status === "active"
                          ? "status-active"
                          : "status-inactive"
                      }`}
                    >
                      {customer.status === "active" ? "Aktif" : "Pasif"}
                    </span>
                  </td>
                  <td>
                    {new Date(customer.createdAt).toLocaleDateString("tr-TR")}
                  </td>
                  <td className="actions-cell">
                    <button
                      className="icon-btn"
                      onClick={() =>
                        navigate(`/admin/customers/${customer._id}/sessions`)
                      }
                      title="Seanslar"
                    >
                      📋
                    </button>
                    <button
                      className="icon-btn"
                      onClick={() => handleOpenChildDialog(customer._id)}
                      title="Çocuk Ekle"
                    >
                      👶
                    </button>
                    <button
                      className="icon-btn icon-btn-edit"
                      onClick={() => handleOpenDialog(customer)}
                      title="Düzenle"
                    >
                      ✏️
                    </button>
                    <button
                      className="icon-btn icon-btn-delete"
                      onClick={() => {
                        setCustomerToDelete(customer._id);
                        setConfirmDelete(true);
                      }}
                      title="Sil"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredCustomers.length === 0 && (
            <div className="empty-state">
              <p>
                {searchTerm ? "Arama sonucu bulunamadı" : "Henüz müşteri yok"}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Müşteri Ekleme/Düzenleme Dialog */}
      <Modal isOpen={openDialog} onClose={handleCloseDialog}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{editingCustomer ? "Müşteri Düzenle" : "Yeni Müşteri"}</h2>
            <button className="modal-close" onClick={handleCloseDialog}>
              ✕
            </button>
          </div>
          <div className="modal-body">
            <div className="form-group">
              <label htmlFor="fullName">
                Ad Soyad <span className="required">*</span>
              </label>
              <input
                id="fullName"
                type="text"
                value={formData.fullName}
                onChange={(e) =>
                  setFormData({ ...formData, fullName: e.target.value })
                }
                placeholder="Ad Soyad"
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone">
                Telefon <span className="required">*</span>
              </label>
              <input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                placeholder="05XX XXX XX XX"
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">E-posta</label>
              <input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                placeholder="ornek@email.com"
              />
            </div>

            <div className="form-group">
              <label htmlFor="status">Durum</label>
              <select
                id="status"
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
              >
                <option value="active">Aktif</option>
                <option value="inactive">Pasif</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="address">Adres</label>
              <textarea
                id="address"
                rows="2"
                value={formData.address}
                onChange={(e) =>
                  setFormData({ ...formData, address: e.target.value })
                }
                placeholder="Müşteri adresi..."
              />
            </div>

            <div className="form-group">
              <label htmlFor="notes">Notlar</label>
              <textarea
                id="notes"
                rows="3"
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                placeholder="Müşteri hakkında özel notlar..."
              />
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={handleCloseDialog}>
              İptal
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={!formData.fullName || !formData.phone}
            >
              {editingCustomer ? "Güncelle" : "Oluştur"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Çocuk Ekleme Dialog */}
      <Modal isOpen={openChildDialog} onClose={() => setOpenChildDialog(false)}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Çocuk Ekle</h2>
            <button
              className="modal-close"
              onClick={() => setOpenChildDialog(false)}
            >
              ✕
            </button>
          </div>
          <div className="modal-body">
            <div className="form-group">
              <label htmlFor="childName">
                Çocuk Adı <span className="required">*</span>
              </label>
              <input
                id="childName"
                type="text"
                value={childFormData.name}
                onChange={(e) =>
                  setChildFormData({ ...childFormData, name: e.target.value })
                }
                placeholder="Çocuk Adı"
              />
            </div>

            <div className="form-group">
              <label htmlFor="birthDate">Doğum Tarihi</label>
              <input
                id="birthDate"
                type="date"
                value={childFormData.birthDate}
                onChange={(e) =>
                  setChildFormData({
                    ...childFormData,
                    birthDate: e.target.value,
                  })
                }
              />
            </div>

            <div className="form-group">
              <label htmlFor="gender">Cinsiyet</label>
              <select
                id="gender"
                value={childFormData.gender}
                onChange={(e) =>
                  setChildFormData({ ...childFormData, gender: e.target.value })
                }
              >
                <option value="">Belirtilmedi</option>
                <option value="male">Erkek</option>
                <option value="female">Kız</option>
                <option value="other">Diğer</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="diagnosis">Tanı/Hastalık</label>
              <input
                id="diagnosis"
                type="text"
                value={childFormData.diagnosis}
                onChange={(e) =>
                  setChildFormData({
                    ...childFormData,
                    diagnosis: e.target.value,
                  })
                }
                placeholder="Varsa tanı veya hastalık bilgisi..."
              />
            </div>

            <div className="form-group">
              <label htmlFor="medicalNotes">Tıbbi Notlar</label>
              <textarea
                id="medicalNotes"
                rows="2"
                value={childFormData.medicalNotes}
                onChange={(e) =>
                  setChildFormData({
                    ...childFormData,
                    medicalNotes: e.target.value,
                  })
                }
                placeholder="Tıbbi geçmiş, alerjiler vb..."
              />
            </div>

            <div className="form-group">
              <label htmlFor="specialNeeds">Özel İhtiyaçlar</label>
              <textarea
                id="specialNeeds"
                rows="2"
                value={childFormData.specialNeeds}
                onChange={(e) =>
                  setChildFormData({
                    ...childFormData,
                    specialNeeds: e.target.value,
                  })
                }
                placeholder="Özel ihtiyaçlar veya dikkat edilmesi gerekenler..."
              />
            </div>

            <div className="form-group">
              <label htmlFor="childNotes">Genel Notlar</label>
              <textarea
                id="childNotes"
                rows="2"
                value={childFormData.notes}
                onChange={(e) =>
                  setChildFormData({ ...childFormData, notes: e.target.value })
                }
                placeholder="Genel notlar..."
              />
            </div>
          </div>
          <div className="modal-footer">
            <button
              className="btn btn-secondary"
              onClick={() => setOpenChildDialog(false)}
            >
              İptal
            </button>
            <button
              className="btn btn-primary"
              onClick={handleChildSubmit}
              disabled={!childFormData.name}
            >
              Ekle
            </button>
          </div>
        </div>
      </Modal>

      {/* Toplu SMS Dialog */}
      <Modal
        isOpen={openBulkSmsDialog}
        onClose={() => setOpenBulkSmsDialog(false)}
      >
        <div
          className="modal-content modal-sm"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-header">
            <h2>Toplu SMS Gönder</h2>
            <button
              className="modal-close"
              onClick={() => setOpenBulkSmsDialog(false)}
            >
              ✕
            </button>
          </div>
          <div className="modal-body">
            <p className="sms-info">
              {selectedCustomers.length} müşteriye SMS gönderilecek
            </p>
            <div className="form-group">
              <label htmlFor="smsMessage">Mesajınız</label>
              <textarea
                id="smsMessage"
                rows="5"
                value={bulkSmsMessage}
                onChange={(e) => setBulkSmsMessage(e.target.value)}
                placeholder="Toplu SMS mesajınızı yazın..."
              />
              <small className="helper-text">
                {bulkSmsMessage.length} karakter
              </small>
            </div>
          </div>
          <div className="modal-footer">
            <button
              className="btn btn-secondary"
              onClick={() => setOpenBulkSmsDialog(false)}
            >
              İptal
            </button>
            <button
              className="btn btn-primary"
              onClick={handleBulkSms}
              disabled={!bulkSmsMessage.trim()}
            >
              <span className="btn-icon">📨</span>
              Gönder
            </button>
          </div>
        </div>
      </Modal>

      {/* Silme Onayı Dialog */}
      <Modal isOpen={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <div
          className="modal-content modal-sm"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-header">
            <h2>Müşteriyi Sil</h2>
            <button
              className="modal-close"
              onClick={() => setConfirmDelete(false)}
            >
              ✕
            </button>
          </div>
          <div className="modal-body">
            <p>
              Bu müşteriyi silmek istediğinizden emin misiniz? Bu işlem geri
              alınamaz ve müşterinin tüm çocuk kayıtları da silinecektir.
            </p>
          </div>
          <div className="modal-footer">
            <button
              className="btn btn-secondary"
              onClick={() => setConfirmDelete(false)}
            >
              İptal
            </button>
            <button className="btn btn-danger" onClick={handleDeleteConfirm}>
              Sil
            </button>
          </div>
        </div>
      </Modal>

      {/* Toast */}
      <Snackbar
        open={toast.show}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, show: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={toast.type}>{toast.message}</Alert>
      </Snackbar>
    </div>
  );
};

export default AdminCustomersPage;
